// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerJoinFieldDropdown.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Join field Dropdown component
 */

import React, { Component, PropTypes } from "react";
import { action, observe } from "mobx";
import { observer } from "mobx-react";
import constants from "logic/data/Izenda.ReportDesignerConstants";
import DropDown from "share/components/controls/Izenda.DropDown";
import Validator from "share/components/hoc/Izenda.Validator";
import { find, isNil, isEmpty, map, each } from "lodash/fp";
const DropDownValidator = Validator()(DropDown);
import connect from "share/enhancers/Izenda.Connect";

@connect()
export default class ReportDesignerJoinFieldDropdown extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    dataSourceItem: PropTypes.object.isRequired,
    dataObjectKey: PropTypes.string.isRequired,
    categoryKey: PropTypes.string.isRequired,
    getDataObjects: PropTypes.func,
    getDropdownOptions: PropTypes.func,
    column: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.oldValue = null;
    this.newValue = null;

    const { dataSourceItem, column: { key } } = props;
    this.joinFieldObserve = observe(dataSourceItem, key, (newValue, oldValue) => {
      this.oldValue = oldValue;
      this.newValue = newValue;
    });
  }

  @action("changeJoinField")
  changeJoinField = (object: any) => {
    const { store, dataSourceItem, column, column: { key, props: { labelKey } }, onChange, dataObjectKey } = this.props;

    const isJoinField = dataObjectKey === constants.TABLE_DEF.ADVANCED_MODE.dataObject.key;

    dataSourceItem[key] = object.value;
    dataSourceItem[labelKey] = object.label;
    onChange && onChange(object.value);

    dataSourceItem.isKeyJoin
      ? dataSourceItem.updateKeyJoinItem(key, object.value)
      : dataSourceItem.updateRelationshipItem(key, object.value);
    store.reportStore.dataSourceStore.dataSourceDetail.markDataSourceGridDirty();

    // Reset comparison value when change join field
    if (isJoinField) {
      dataSourceItem.setComparisionOperator &&
        dataSourceItem.setComparisionOperator(constants.DEFAULT_COMPARISION_OPERATOR);
    }
  };

  componentDidUpdate() {
    // Need to reset validation indicator if its value changed from NULL to a not NULL value.
    if (isNil(this.oldValue) && !isNil(this.newValue)) {
      const { dataObjectKey, validation, dataSourceItem } = this.props;
      const key =
        dataObjectKey === constants.TABLE_DEF.ADVANCED_MODE.dataObject.key
          ? constants.TABLE_DEF.ADVANCED_MODE.joinField.key
          : constants.TABLE_DEF.ADVANCED_MODE.foreignJoinfield.key;
      validation.validate(key, "Relationships" + dataSourceItem.tempId);
    }
  }

  componentWilUnmount() {
    this.joinFieldObserve();
  }

  render() {
    const {
      dataSourceItem,
      dataObjectKey,
      categoryKey,
      store,
      column: { key },
      getDataObjects,
      getDropdownOptions
    } = this.props;

    const joinFieldKey = constants.TABLE_DEF.ADVANCED_MODE.joinField.key;
    const selectedJoinSourceAlias = constants.TABLE_DEF.ADVANCED_MODE.selectedJoinSourceAlias.key;
    const selectedForeignSourceAlias = constants.TABLE_DEF.ADVANCED_MODE.selectedForeignSourceAlias.key;
    const isFieldReady = key == joinFieldKey ? !dataSourceItem.isJoinFieldReady : !dataSourceItem.isForeignFieldReady;
    const isCrossJoin = dataSourceItem.joinType === constants.JOIN_TYPE_CROSS;

    const dataObjects = getDataObjects(dataSourceItem, categoryKey, store, dataSourceItem.isKeyJoin);
    let currentDataObject;
    // There are two cases:
    // Case 1: is keyjoin
    if (dataSourceItem.isKeyJoin) {
      currentDataObject =
        dataObjectKey === constants.TABLE_DEF.ADVANCED_MODE.dataObject.key
          ? find({ aliasId: dataSourceItem[selectedJoinSourceAlias] }, dataObjects)
          : find({ aliasId: dataSourceItem[selectedForeignSourceAlias] }, dataObjects);
      // Case 2: is relationship
    } else {
      currentDataObject =
        dataObjectKey === constants.TABLE_DEF.ADVANCED_MODE.foreignDataObject.key
          ? find({ aliasId: dataSourceItem[dataObjectKey] }, dataObjects)
          : find({ id: dataSourceItem[dataObjectKey] }, dataObjects);
    }

    currentDataObject = currentDataObject || {}; // prevent null object

    const options = isNil(currentDataObject) ? [] : getDropdownOptions(currentDataObject.fields, false, false);
    const selectedItems = find({ id: dataSourceItem[key] }, currentDataObject.fields);
    const isMaskedHiddenForField =
      currentDataObject && dataSourceItem[key] && (isEmpty(selectedItems) || selectedItems.inaccessible);
    const validationRule = isCrossJoin ? {} : constants.REQUIRED_CONSTRAINT;

    return (
      <DropDownValidator
        options={options}
        onChange={this.changeJoinField}
        value={dataSourceItem[key]}
        disabled={isFieldReady || isCrossJoin}
        maskedHidden={isMaskedHiddenForField}
        valueField="value"
        labelField="label"
        blankOption
        hideIcon
        showTooltip
        validations={validationRule}
        field={key}
        group={"Relationships" + dataSourceItem.tempId}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/DataSource/Izenda.ReportDesignerJoinFieldDropdown.js